<script setup lang="ts">
import { screens as defaultBreakpoints } from "tailwindcss/defaultTheme"

import { onMounted, ref, computed, watch } from "vue"
import { useQuery } from "@tanstack/vue-query"
import { User, ChevronRight, Search } from "lucide-vue-next"

import { cn } from "@/lib/utils"
import { listAuthors, getLocalizedAbstract } from "@/lib/article.ts"

import { type Author, getAuthors, getAuthorArticles, authorName } from "@/models/author"

import Input from "@/components/ui/input/Input.vue"
import Separator from "@/components/ui/separator/Separator.vue"
import ListBox from "@/components/ui-custom/list-box/index.vue"

import DetailContainer from "@/views/layouts/components/DetailContainer.vue"

const { data: authors } = useQuery({
  queryKey: ["authors"],
  queryFn: getAuthors,
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const selectedAuthor = ref<Author | undefined | null>()
const selectedAuthorId = computed(() => selectedAuthor.value?.id)
const searchValue = ref("")

const { data: articles } = useQuery({
  queryKey: ["authors", selectedAuthorId, "articles"],
  queryFn: ({ signal }) => getAuthorArticles({ signal: signal, id: selectedAuthorId.value }),
  enabled: computed(() => !!selectedAuthorId.value),
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const filteredAuthors = computed(() => {
  if (!searchValue.value) return authors.value
  return authors.value?.filter(
    (author) =>
      author.name.toLowerCase().includes(searchValue.value.toLowerCase()) ||
      author.surname.toLowerCase().includes(searchValue.value.toLowerCase()) ||
      `${author.surname} ${author.name}`.toLowerCase().includes(searchValue.value.toLowerCase()) ||
      `${author.name} ${author.surname}`.toLowerCase().includes(searchValue.value.toLowerCase())
  )
})

const selectAuthor = (author: Author) => {
  selectedAuthor.value = author

  goToSlide(1)
}

watch(filteredAuthors, (newValue) => {
  selectedAuthor.value = newValue?.length ? newValue[0] : undefined
})

const firstSlidingPanel = ref<HTMLDivElement | null>(null)

const goToSlide = (number: number) => {
  if (window.innerWidth >= parseInt(defaultBreakpoints.md)) {
    return
  }

  if (!firstSlidingPanel.value) return

  firstSlidingPanel.value.style.marginLeft = `${number * -100}%`
}

const resetSelected = () => {
  if (filteredAuthors.value?.length && selectedAuthor) {
    selectedAuthor.value = filteredAuthors.value[0]
    searchValue.value = ''
  }

  goToSlide(0)
}

onMounted(() => {
  if (filteredAuthors.value?.length) selectedAuthor.value = filteredAuthors.value[0]
})
</script>

<template>
  <div id="navbar" class="container border-white border-t-10 border-b-10 text-primary text-sm">
    <span class="px-2">
      <user class="cursor-pointer inline" @click="resetSelected" />  <chevron-right class="inline" />
      <span v-if="selectedAuthor">{{ selectedAuthor.surname }} {{ selectedAuthor.name }}</span>
    </span>
  </div>

  <detail-container class="container flex flex-col">
    <div class="flex h-[800px] flex-1 overflow-hidden" v-if="authors">
      <div class="sliding-panel md:!ml-0" ref="firstSlidingPanel">
        <div class="flex flex-0 p-2 text-primary">{{ $t("authors.select") }}</div>

        <div class="flex flex-1 flex-col overflow-y-auto border border-r-0 bg-muted p-1">
          <div class="flex-0 flex p-1">
            <form class="relative flex flex-1">
              <Search class="absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input v-model="searchValue" :placeholder='`${$t("commons.search")}...`'  class="bg-white pl-8" />
            </form>
          </div>

          <div class="flex flex-1 flex-col gap-1 overflow-y-auto bg-muted p-1">
            <div
              v-for="author in filteredAuthors"
              :key="author.id"
              :class="
                cn([
                  'flex cursor-pointer border bg-white p-2 hover:bg-primary hover:text-white hover:transition-all hover:duration-300',
                  selectedAuthor === author ? 'bg-primary text-white transition-all duration-300' : ''
                ])
              "
              @click="selectAuthor(author)">
              <div class="flex-1">{{ author.surname }} {{ author.name }}</div>
              <div class="flex min-w-10 justify-center bg-muted px-2 text-foreground">
                {{ author.articles_count }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="sliding-panel">
        <div class="flex flex-0 p-2 text-primary">{{ $t("authors.selected") }}:</div>

        <div class="flex flex-1 flex-col gap-2 overflow-y-auto border border-l-0 bg-muted p-1">

          <span
            class="flex-0 flex p-1"
            v-if="selectedAuthor"
          >

              <span class="flex min-h-32 flex-1 gap-4 border bg-white p-2">
                <span class="flex flex-1 flex-col">
                  <span class="font-semibold text-primary">{{ authorName(selectedAuthor) }}</span>

                  <Separator />

                  <span class="flex flex-1 flex-col py-2">
                    <span class="flex-0 flex pb-2" v-if="selectedAuthor.biography">
                      <span>{{ selectedAuthor.biography }}</span>
                    </span>

                    <span class="flex-0 flex">
                      <span class="mr-4 w-[70px] text-primary">
                        {{ $t("publications.articles") }}:
                      </span>

                      <span>{{ selectedAuthor.articles_count }}</span>
                    </span>
                  </span>
                </span>

              </span>

          </span>

          <Separator />

          <div class="flex flex-1 flex-col gap-1 overflow-y-auto">
            <list-box
                :url="{ name: 'show_article_path', params: { id: article.id } }"
                :key="`article-${article.id}`"
                class="p-1"
                v-for="article in articles"
              >
                <template #title>
                  [{{ $t("commons.year") }} {{ article.publication_year }} -
                  {{ $t("commons.number") }} {{ article.publication_number }}] - {{ article.title }}
                </template>

                <template #default>
                  <div class="flex-0 text-gray-600 py-1" v-if="article?.authors">
                    {{ $t('commons.by') }} <span class="font-semibold">{{ listAuthors(article.authors) }}</span>
                  </div>

                  <div class="line-clamp-2 flex-1" v-if="getLocalizedAbstract(article)">{{ getLocalizedAbstract(article) }}</div>
                </template>
              </list-box>
          </div>
        </div>
      </div>
    </div>
  </detail-container>
</template>
